/**
 * Fravær for the employee (owner's employee UI brief, 2026-09-22, screen 7), apart from the
 * manager's Fravær. Two tabs kept apart on purpose: Søknader is a request with a status, asked for
 * with the deduction shown before it is sent; Tilgjengelighet is a scheduling constraint the
 * manager sees in the plan. A pending request can be withdrawn; an approved one is the manager's
 * to change, so it can only be asked to be cancelled (owner, 2026-09-21).
 */
import { errorText } from '@/api/client';
import {
  useMe,
  useMyAbsenceTypes,
  useMyAbsences,
  useMyAvailabilities,
  useMyWrites,
  useTimeBank,
  useWorkTimeChanges,
} from '@/api/data';
import { useCan } from '@/auth/caps';
import { useAuth } from '@/auth/store';
import { T } from '@/i18n';
import { oslo, osloISO, today } from '@/rules';
import { useTenant } from '@/shell/tenant';
import { Button, Chip, Empty, toast } from '@/ui';
import { DateInput } from '@/ui/DateInput';
import { fmtD, fmtDs, num, typeName } from '@/ui/format';
import { Icon } from '@/ui/icons';
import { addDays } from '@timefly/core';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { StatusChip } from '../ansatt/TilgjengelighetTab';
import {
  RangeFields,
  bodyOf,
  describe,
  explain,
  kindOf,
  kindOfAbsence,
  rangeOf,
} from '../fravaer/AbsenceRange';
import { DeductionLine, useDeductionState } from '../fravaer/Deduction';
import { catInfo, catOf } from '../fravaer/category';
import { cancelIdsOf } from '../fravaer/queue';
import { Card, Progress } from './parts';

const run = async (ok: string, f: () => Promise<unknown>) => {
  try {
    await f();
    toast(T(ok));
  } catch (e) {
    toast(errorText(e));
  }
};

function Soknader() {
  const user = useAuth((s) => s.user);
  const can = useCan();
  const day0 = today();
  const y = Number(day0.slice(0, 4));
  // this year and next, so a request for January made in November is still in the list
  const absQ = useMyAbsences(`${y}-01-01`, `${y + 1}-12-31`);
  const typesQ = useMyAbsenceTypes();
  const meQ = useMe(user?.id);
  const w = useMyWrites();
  const mineQ = useWorkTimeChanges({ mine: true });
  // avspasering draws on the person's own timebank (plan 0027 G2), so the form says what is in it
  const bank = useTimeBank('mine').data;
  const [abs, setAbs] = useState({ type: 0, from: day0, fromT: '', to: day0, toT: '', reason: '' });
  const [err, setErr] = useState('');
  const [all, setAll] = useState(false);
  const types = (typesQ.data ?? []).filter((t) => t.is_active);
  const absType = types.find((t) => t.id === abs.type) ?? types[0];
  const kind = kindOf(absType?.type);
  const body = bodyOf(abs, kind);
  // what approval will take from the vacation balance, from the server, before Send (Codex, 2026-09-14)
  const deduction = useDeductionState(
    'start_date' in body
      ? { absence_type_id: absType?.id ?? null, start_date: body.start_date, end_date: body.end_date }
      : null,
    !!absType?.is_deductible,
  );
  const list = (absQ.data ?? []).sort((a, b) => b.start_date.localeCompare(a.start_date));
  const cancelAsked = cancelIdsOf(mineQ.data ?? [], absQ.data ?? []);
  const me = meQ.data;
  const edit = (p: Partial<typeof abs>) => {
    setErr('');
    // a new type is a new kind of range: fold back to one day and empty clocks
    setAbs((x) =>
      p.type !== undefined && p.type !== x.type
        ? { ...x, ...p, to: x.from, fromT: '', toT: '' }
        : { ...x, ...p },
    );
  };
  const send = async () => {
    const range = bodyOf(abs, kind);
    if ('problem' in range) {
      setErr(range.problem);
      return;
    }
    try {
      await w.requestAbsence.mutateAsync({
        absence_type_id: absType?.id as number,
        ...range,
        reason: abs.reason || null,
      });
      toast(T('Søknad sendt. Lederen ser den under Oppfølging.'));
      setAbs((x) => ({ ...x, reason: '' }));
    } catch (e) {
      setErr(explain(e));
    }
  };

  return (
    <div className="egrid">
      <div>
        <Card title={T('Mine søknader')} sub={T('i år og neste år')} testid="my-absences">
          {list.length ? (
            (all ? list : list.slice(0, 8)).map((a) => {
              const c = catInfo(catOf(a.absence_type));
              return (
                <div className="erow static" key={a.id} data-testid="my-absence">
                  <span
                    className="eabsic box"
                    style={{ background: `var(--${c.tone}-f)`, color: `var(--${c.tone}-i)` }}
                    aria-hidden="true"
                  >
                    <Icon name={c.icon} />
                  </span>
                  <span className="ebody">
                    <b>{typeName(a.absence_type)}</b>
                    <span>
                      {describe(rangeOf(a), kindOfAbsence(a, typesQ.data ?? []))}
                      {a.reason ? ` · ${a.reason}` : ''}
                    </span>
                  </span>
                  {a.status === 'pending' ? (
                    <Chip tone="warn" small>
                      {T('Venter')}
                    </Chip>
                  ) : a.status === 'accepted' ? (
                    <Chip tone="ok" small>
                      {T('Godkjent')}
                    </Chip>
                  ) : (
                    <Chip tone="dgr" small>
                      {T('Avslått')}
                    </Chip>
                  )}
                  {a.status === 'pending' ? (
                    <Button
                      small
                      onClick={() => run('Søknaden er trukket', () => w.withdrawAbsence.mutateAsync(a.id))}
                    >
                      {T('Trekk')}
                    </Button>
                  ) : a.status === 'accepted' && cancelAsked.has(a.id) ? (
                    <span className="hint" style={{ margin: 0 }} data-testid="cancel-asked">
                      {T('Du har bedt om å avlyse')}
                    </span>
                  ) : a.status === 'accepted' && (oslo(a.end_date)?.date ?? '') >= day0 ? (
                    <Button
                      small
                      onClick={() =>
                        run('Lederen har fått beskjed om at du vil avlyse', () =>
                          w.askCancel.mutateAsync({ id: a.id }),
                        )
                      }
                    >
                      {T('Be om å avlyse')}
                    </Button>
                  ) : (
                    <span />
                  )}
                </div>
              );
            })
          ) : (
            <p className="hint" style={{ margin: 0 }}>
              {T('Ingen søknader ennå.')}
            </p>
          )}
          {list.length > 8 && !all && (
            <button type="button" className="btn w" onClick={() => setAll(true)}>
              {T('Vis flere søknader ({n})', { n: list.length - 8 })}
            </button>
          )}
        </Card>
        {me && me.vacation_days_balance != null && (
          <Card title={T('Feriesaldo')} testid="balance">
            <p className="ebal">
              <b>{T('{n} dager igjen', { n: me.vacation_days_balance })}</b>
              {me.total_vacation_days_per_year != null && (
                <span> {T('av {n} dager i {year}', { n: me.total_vacation_days_per_year, year: y })}</span>
              )}
            </p>
            {me.total_vacation_days_per_year ? (
              <Progress done={me.vacation_days_balance} total={me.total_vacation_days_per_year} ok />
            ) : null}
            {me.next_year_vacation_days_balance != null && (
              <p className="hint">{T('Neste år: {n} dager', { n: me.next_year_vacation_days_balance })}</p>
            )}
          </Card>
        )}
      </div>
      <Card title={T('Ny søknad')} testid="new-absence">
        {/* GET /absence-types answers [] when the company has none: a setup gap, not a choice left unmade */}
        {typesQ.isError ? (
          <div className="al dgr" role="alert">
            <span>
              <Icon name="stop" />
            </span>
            <span>{T('Kunne ikke hente fraværstypene. Prøv igjen om litt.')}</span>
          </div>
        ) : typesQ.isSuccess && types.length === 0 ? (
          <div className="al info" data-testid="absence-no-types">
            <span>
              <Icon name="info" />
            </span>
            <span>
              {T('Bedriften har ingen fraværstyper ennå, så det kan ikke søkes om fravær her.')}{' '}
              {can('innstillinger.se') ? (
                <Link to="/innstillinger/typer">{T('Legg inn fraværstyper under Innstillinger.')}</Link>
              ) : (
                T('Lederen legger dem inn under Innstillinger, Vakt- og fraværstyper.')
              )}
            </span>
          </div>
        ) : null}
        <div className="fld">
          <span className="lbl">{T('Type fravær')}</span>
          <select
            className="inp w"
            aria-label={T('Fraværstype')}
            value={absType?.id ?? ''}
            onChange={(e) => edit({ type: Number(e.target.value) })}
            disabled={types.length === 0}
          >
            {types.length === 0 && (
              <option value="">{typesQ.isPending ? T('Henter…') : T('Ingen fraværstyper')}</option>
            )}
            {types.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </div>
        <RangeFields kind={kind} value={abs} onChange={(r) => edit(r)} />
        {absType?.key === 'avspasering' && bank && (
          <p className="hint" data-testid="timebank-balance">
            {bank.agreed_on
              ? T('Du har {h} t i timebanken.', { h: num(bank.balance) })
              : T(
                  'Du har ingen skriftlig avtale om avspasering, så det er ingen timebank å ta av. Snakk med lederen.',
                )}
          </p>
        )}
        <div className="fld">
          <span className="lbl">{T('Merknad (valgfritt)')}</span>
          <textarea
            className="inp w"
            rows={3}
            maxLength={500}
            aria-label={T('Merknad')}
            value={abs.reason}
            onChange={(e) => edit({ reason: e.target.value })}
          />
        </div>
        <DeductionLine state={deduction} timed={kind !== 'date'} />
        {err && (
          <div className="al dgr" role="alert" data-testid="absence-problem">
            <span>
              <Icon name="stop" />
            </span>
            <span>{err}</span>
          </div>
        )}
        <Button
          tone="pri"
          className="w"
          disabled={
            !absType ||
            w.requestAbsence.isPending ||
            // held only while a valid range is being priced; an invalid range still gets its refusal on Send
            ('start_date' in body && !!absType?.is_deductible && deduction.status !== 'ready')
          }
          onClick={send}
        >
          <Icon name="upload" />
          {T('Send søknad')}
        </Button>
      </Card>
    </div>
  );
}

/**
 * Days the person cannot work, or can only work part of. One entry per day here; a repeating rule
 * is made on the phone and is listed with its pattern, so the list already has room for it.
 */
function Tilgjengelighet() {
  const day0 = today();
  const availQ = useMyAvailabilities();
  const w = useMyWrites();
  const asks = !!useTenant().tenant?.availability_requires_approval;
  const [av, setAv] = useState({ date: addDays(day0, 1), from: '', to: '' });
  const partial = !!(av.from && av.to);
  const list = (availQ.data ?? [])
    .filter((a) => (oslo(a.end_date_time)?.date ?? '') >= day0)
    .sort((a, b) => a.start_date_time.localeCompare(b.start_date_time));
  return (
    <div className="egrid">
      <Card
        title={T('Når kan du ikke jobbe?')}
        sub={asks ? T('lederen ser det i vaktplanen og godkjenner det') : T('lederen ser det i vaktplanen')}
        testid="availability-form"
      >
        <div className="fld">
          <span className="lbl">{T('Dato')}</span>
          <DateInput
            className="inp"
            aria-label={T('Dato')}
            value={av.date}
            onValueChange={(value) => setAv({ ...av, date: value })}
          />
        </div>
        <div className="fld">
          <span className="lbl">{T('Kun mellom (valgfritt)')}</span>
          <div className="two">
            <input
              className="inp"
              type="time"
              aria-label={T('Kun fra')}
              value={av.from}
              onChange={(e) => setAv({ ...av, from: e.target.value })}
            />
            <input
              className="inp"
              type="time"
              aria-label={T('Kun til')}
              value={av.to}
              onChange={(e) => setAv({ ...av, to: e.target.value })}
            />
          </div>
        </div>
        <p className="hint">
          {partial
            ? T('Du kan bare jobbe {from}–{to} denne dagen.', { from: av.from, to: av.to })
            : T('Uten klokkeslett blokkeres hele dagen.')}
        </p>
        <Button
          tone="pri"
          disabled={!av.date || w.addAvailability.isPending}
          onClick={() =>
            run(
              partial
                ? T('Kun {from}–{to} {day}', { from: av.from, to: av.to, day: fmtDs(av.date) })
                : T('Ikke tilgjengelig {day}', { day: fmtDs(av.date) }),
              () =>
                w.addAvailability.mutateAsync(
                  partial
                    ? {
                        start_date_time: osloISO(av.date, av.from),
                        end_date_time: osloISO(av.date, av.to),
                        is_available: true,
                        is_recurring: false,
                      }
                    : {
                        start_date_time: osloISO(av.date, '00:00'),
                        end_date_time: osloISO(av.date, '23:59'),
                        is_available: false,
                        is_recurring: false,
                      },
                ),
            )
          }
        >
          {partial ? T('Legg inn delvis') : T('Blokker dagen')}
        </Button>
      </Card>
      <Card title={T('Mine begrensninger')} sub={T('framover')} testid="availability-list">
        {list.length ? (
          list.map((a) => (
            <div className="erow static" key={a.id} data-testid="my-availability">
              <span className="eabsic box" aria-hidden="true">
                <Icon name={a.is_available ? 'clock' : 'calendarOff'} />
              </span>
              <span className="ebody">
                <b>
                  {fmtD(oslo(a.start_date_time)?.date ?? '')}
                  {a.is_recurring
                    ? `, ${a.recurrence_pattern === 'weekly' ? T('hver uke') : T('hver dag')}`
                    : ''}
                </b>
                <span>
                  {a.is_available
                    ? `${a.is_preference ? T('ønsker å jobbe') : T('kun')} ${oslo(a.start_date_time)?.time}–${oslo(a.end_date_time)?.time}`
                    : T('ikke tilgjengelig')}
                </span>
                {/* issue #70: while the company asks for approval, the person sees where it stands */}
                {asks && <StatusChip a={a} />}
              </span>
              <Button small onClick={() => run('Fjernet', () => w.removeAvailability.mutateAsync(a.id))}>
                {T('Fjern')}
              </Button>
            </div>
          ))
        ) : (
          <Empty>{T('Ingen begrensninger framover.')}</Empty>
        )}
      </Card>
    </div>
  );
}

export default function MittFravaer() {
  const [params, setParams] = useSearchParams();
  const tab = params.get('fane') === 'tilgjengelighet' ? 'tilgjengelighet' : 'soknader';
  return (
    <>
      <div className="ph">
        <div>
          <h1>{T('Fravær')}</h1>
          <div className="sub">{T('Søknader og tilgjengelighet')}</div>
        </div>
      </div>
      <div className="seg etabs" role="tablist" aria-label={T('Fravær')}>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'soknader'}
          aria-pressed={tab === 'soknader'}
          onClick={() => setParams({})}
        >
          {T('Søknader')}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'tilgjengelighet'}
          aria-pressed={tab === 'tilgjengelighet'}
          onClick={() => setParams({ fane: 'tilgjengelighet' })}
        >
          {T('Tilgjengelighet')}
        </button>
      </div>
      {tab === 'soknader' ? <Soknader /> : <Tilgjengelighet />}
    </>
  );
}
